if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        /*
         * Main gallery arrows (mobile stack / no thumb strip): SliderComponent scroll math can
         * disable next early when there is no overflow. Same as thumbnail arrows - switch media explicitly.
         */
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('click', this.onViewerArrowClick.bind(this), true);
        }

        if (!this.elements.thumbnails) {
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
          return;
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        /*
         * Dawn’s thumbnail arrows only horizontal-scroll the thumb strip (SliderComponent).
         * Shoppers expect them to advance the main gallery - same as clicking the next/previous thumb.
         * Capture phase runs before SliderComponent so we switch media instead of scrolling.
         */
        this.elements.thumbnails.addEventListener(
          'click',
          (e) => {
            const btn = e.target.closest('button.slider-button--prev, button.slider-button--next');
            if (!btn) return;
            const items = Array.from(this.elements.thumbnails.querySelectorAll('.thumbnail-list__item'));
            const currentIdx = items.findIndex((li) => li.querySelector('button[aria-current="true"]'));
            if (currentIdx < 0 || items.length < 2) return;
            const delta = btn.classList.contains('slider-button--next') ? 1 : -1;
            const nextIdx = currentIdx + delta;
            if (nextIdx < 0 || nextIdx >= items.length) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            items[nextIdx].querySelector('button')?.click();
          },
          true
        );
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onViewerArrowClick(e) {
        if (!this.elements.viewer) return;
        const btn = e.target.closest('button[name="previous"], button[name="next"]');
        if (!btn || btn.closest('[id^="GalleryThumbnails"]')) return;
        if (btn.disabled) return;

        const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        if (slides.length < 2) return;

        const currentIdx = slides.findIndex((li) => li.classList.contains('is-active'));
        if (currentIdx < 0) return;

        const delta = btn.name === 'next' ? 1 : -1;
        const nextIdx = currentIdx + delta;
        if (nextIdx < 0 || nextIdx >= slides.length) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        this.setActiveMedia(slides[nextIdx].dataset.mediaId, false);
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset?.mediaId;
        if (mediaId && this.elements.viewer) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((node) => {
            node.classList.toggle('is-active', node.dataset.mediaId === mediaId);
          });
        }
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        const position = activeThumbnail?.dataset?.mediaPosition;
        if (position) this.announceLiveRegion(activeMedia, position);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
